import { InjectionToken, ValueProvider } from '@angular/core';
import {
  API_DEVELOPMENT_ACTUAR,
  APP_URL_DEVELOPMENT,
  EXTERNAL_URL_DEVELOPMENT,
} from './env/environment.development';
import {
  API_PRODUCTION_ACTUAR,
  APP_URL_PRODUCTION,
  EXTERNAL_URL_PRODUCTION,
} from './env/environment.production';
import {
  API_LOCAL_ACTUAR,
  APP_URL_LOCAL,
  EXTERNAL_URL_LOCAL,
} from './env/environment';
import { APP_URL_STAGE, EXTERNAL_URL_STAGE } from './env/environment.stage';
import {
  API_RELEASE_ACTUAR,
  APP_URL_RELEASE,
  EXTERNAL_URL_RELEASE,
} from './env/environment.release';
import { EnabledPlatformFeatures } from './platform-features';

export type Environment = 'local' | 'dev' | 'stage' | 'prod' | 'rc';
export type AppId =
  | 'ediz'
  | 'actuar'
  | 'toletus'
  | 'facilfit'
  | 'tickou'
  | 'livepickup'
  | 'accessremotecontrol';

export type AppConfig = {
  version: string;
  apiUrl: ApiUrlsType;
  externalUrl: ExternalUrlsType;
  environment: Environment;
  app_id?: AppId;
  title?: string;
  appSource: AppSource;
  appUrl: APP_URLType;
  enabledPlatformFeatures?: EnabledPlatformFeatures;
};

export enum AppSource {
  None = 0,
  Actuar = 1,
  Treino = 2,
  Afig = 3,
  FacilFit = 4,
  PagueLa = 5,
  Toletus = 6,
  CatracaParaAcademia = 7,
  Act = 8,
  Reserva = 9,
  Ediz = 10,
  Tickou = 11,
  EdizApp = 12,
  TickouApp = 13,
  Accounts = 99, //frontend only
}

export enum AppVersion {
  Old = 0,
  New = 1,
}

/**
 * A null/absent AppVersion means "never migrated", so it reads exactly like
 * `Old`: every legacy-only element has to show up for it too. Only ask this
 * where the subject is a tenant — admin scope (`TProfile.AppVersion` of a
 * client) or `OwnerSettings.AppVersion`. For an ordinary person the field is
 * always null and says nothing about the app version.
 */
export function isLegacyAppVersion(appVersion?: AppVersion | null): boolean {
  return appVersion !== AppVersion.New;
}

export const appVersionMap: Record<AppVersion, string> = {
  [AppVersion.Old]: 'APPCONFIG.APPVERSION.LEGACY',
  [AppVersion.New]: 'APPCONFIG.APPVERSION.NEW',
};

export enum App_url_type_by_AppSource_enum {
  'Actuar' = 'actuar_app',
  'Treino' = 'treino_app',
  'Afig' = 'afig_app',
  'FacilFit' = 'facilfit_app',
  'PagueLa' = 'pague_la_app',
  'Accounts' = 'accounts',
  'Ediz' = 'ediz_app',
  'Tickou' = 'tickou_app',
}

export type APP_URLType = {
  accounts: string;
  actuar_app: string;
  /**
   * Actuar app legado (monólito). Distinto de `actuar_app`, que aponta para o
   * app **novo**. Clientes com `AppVersion.Old` ainda operam aqui, e as telas
   * read-only deste app redirecionam para cá.
   *
   * Hoje é `app.actuar.com` em **todos** os ambientes: o legado só existe em
   * produção. A chave existe na mesma para não hardcodar o host nos componentes
   * e para o dia em que houver ambientes próprios.
   */
  legacy_app: string;
  treino_app: string;
  actuar_sign: string;
  afig_app: string;
  actuar_acesso: string;
  facilfit_app: string;
  face_detection_app: string;
  ediz_app: string;
  tickou_app: string;
  live_pickup_app: string;
  /**
   * Guardian-facing Ediz app (`ediz-family-mobile-app`), served on the web by
   * `deploy-ediz-family-mobile-app-pages-{stg,prd}` in `ci-nx-stg-prd.yml`.
   *
   * Only two environments are published, so `development` reuses the stage host
   * and `release` reuses the production one — same shape as `live_pickup_app`.
   * Pointing them at an `app.familia.dev.*` / `app.familia.rc.*` host would name
   * an address no pipeline serves.
   */
  ediz_family_app: string;
  /**
   * O controle de acesso remoto no celular (`access-remote-control-app`),
   * publicado por `deploy-access-remote-control-app-pages-{stg,prd}` em
   * `ci-nx-stg-prd.yml`, no subdomínio `liveaccess`.
   *
   * Fica sob o mesmo domínio da Entrada de cada ambiente: o `redirect_uri` do
   * login só é aceito no mesmo domínio-pai (`isAllowedRedirectUri`). Só stage e
   * produção são publicados, então `development` usa o host da stage e
   * `release` o de produção, como o `ediz_family_app`.
   */
  live_access_app: string;
  /**
   * Pague.lá v2 (`paguela-app-v2`), emoldurado pela aba "Pague.lá" do menu.
   *
   * Não é `externalUrl.pague_la`: essa é a v1 (`app.pague.la`), que não tem a
   * rota `/embed/transacoes`. A v2 tem uma única publicação, `novo.pague.la`,
   * por isso o host é o mesmo nos cinco ambientes — e ela lê a API de produção
   * em todos eles.
   *
   * Fica de fora de `AppUrlOptions` de propósito: essa união lista os destinos
   * do `/transmission`, que recebem a sessão inteira (`TokenId` incluído). A v2
   * recebe só o bearer e a conta, montados pela própria tela.
   */
  paguela_v2_app: string;
};

export type AppUrlOptions =
  | 'accounts'
  | 'actuar_app'
  | 'legacy_app'
  | 'treino_app'
  | 'actuar_sign'
  | 'afig_app'
  | 'actuar_acesso'
  | 'facilfit_app'
  | 'face_detection_app'
  | 'ediz_app'
  | 'tickou_app'
  | 'live_pickup_app'
  | 'ediz_family_app'
  | 'live_access_app';

export type ApiUrlsType = {
  /**
   * Servidores de loopback dos clientes desktop, na máquina do operador.
   *
   * Vivem aqui, e não no service, pela mesma razão que qualquer outra URL: o
   * endereço é configuração, não código. São iguais nos cinco ambientes porque
   * a máquina do operador é a dele em todos — e é aqui que se mudaria a porta
   * se um build de stage do desk passasse a usar outra.
   *
   * Contrato: `accessedge/docs/integracoes/handoff-front-status-local.md`.
   */
  edge_desk_status: string;
  access_desk_status: string;
  print_agent_status: string;

  /**
   * Release catalog of the desktop clients, on the public CDN.
   *
   * Points at the `.appinstaller` — the Windows App Installer XML, whose root
   * `Version` attribute is the published version. It is the **only** source that
   * exists for all three products: `version.txt` is published for PrintAgent
   * alone (EdgeDesk and AccessDesk return 404), and the `.appinstaller` is the
   * very artifact Windows installs, so it cannot drift from what is live.
   *
   * The five environments deliberately share one address: there is a **single
   * release channel**. All three publish pipelines write to the bucket
   * `prd-desktop-releases` (`R2_BUCKET` defaults to it in every workflow) and no
   * stage/dev bucket exists — the alternative hostnames do not even resolve. So
   * a non-production front compares against, and offers, the production build.
   * The day a second channel is published, this is where it splits.
   *
   * The bucket carries a CORS rule allowing the app origins for `GET`/`HEAD`,
   * which is what lets the browser read these addresses at all (applied and
   * verified 2026-09-15). A front served from a hostname that is not on that
   * list reads nothing and shows no prompt — so a new white-label domain needs
   * the rule updated as well as this config.
   */
  edge_desk_release: string;
  access_desk_release: string;
  print_agent_release: string;
  odata: string;
  indications_service: string;
  odata_goods_service: string;
  odata_person_service: string;
  odata_payable_service: string;
  odata_work_service: string;
  odata_crm_service: string;
  odata_generalfinance_service: string;
  odata_account_service: string;
  odata_receivable_service: string;
  odata_email_service: string;
  odata_contract: string;
  odata_user_service: string;
  odata_workout_service: string;
  odata_post_service: string;
  odata_activity_service: string;
  odata_addresses_service: string;
  odata_booking_service: string;
  odata_graduation_service: string;
  odata_subscription_service: string;
  odata_data_service: string;
  odata_physical_assessment_service: string;
  odata_helpdesk_service: string;
  odata_hotsite_service: string;
  odata_consumption_log_service: string;
  odata_access_log_service: string;
  odata_filegroup_service: string;
  odata_access_service: string;
  odata_access_flow_service: string;
  odata_access_directory_service: string;
  odata_school_service: string;
  school_service: string;
  base: string;
  paguela: string;
  api_cep: string;
  auth: string;
  chat: string;
  file_manager: string;
  treino: string;
  facial: string;
  acesso: string;
  caixa: string;
  treasury_service: string;
  helpdesk: string;
  ajustes: string;
  files_api: string;
  contract: string;
  billing: string;
  gympass: string;
  client_notification: string;
  client_device: string;
  invoicer: string;
  acessoLocalhost: string;
  account_service: string;
  userService: string;
  personService: string;
  file_group: string;
  goods: string;
  payable_service: string;
  workService: string;
  crm_service: string;
  generalfinance_service: string;
  receivable_service: string;
  subscription_service: string;
  email_service: string;
  workout_service: string;
  workout_view: string;
  post_service: string;
  activity_service: string;
  activity_tracking_service: string;
  booking_service: string;
  data_service: string;
  addresses_service: string;
  storage_service: string;
  physical_assessment_service: string;
  whatsapp_service: string;
  hotsite: string;
  graduation_service: string;
  totalpass: string;
  storage: string;
  search_service: string;
  file_group_service: string;
  hotsite_service: string;
  credit_assessment_service: string;
  meetings_service: string;
  access_service: string;
  access_directory_service: string;
  consumption_log_service: string;
  consumption_auth_service: string;
  spooler: string;
  access_edge_manager_service: string;
  access_edge_manager_service_grpc: string;
  fingerprint_service: string;
  suggestion_service: string;
  registration_service: string;
  /**
   * `weatherservice-api` — clima atual por coordenada (`GET /weather/current?lat=&lon=`).
   *
   * Deploy único (produção): não há ambiente de homologação separado, então os cinco
   * ambientes apontam para o mesmo host — como `print_agent_service` e `suggestion_service`.
   */
  weather_service: string;
  academy_service: string;
  /** Full OData controller base for the academy service (`/odata/{Set}` — paging/filtering). */
  odata_academy_service: string;
  access_log_service: string;
  access_flow_service: string;
  integration_api: string;
  /** `migrator-api` (data-migration intake). Host `migrator-api.{env}.g.actuar.cloud`. */
  migrator_service: string;
  /** PrintAgentService — impressão via agentes instalados nas máquinas do cliente. */
  print_agent_service: string;
  /** Link na Bio — Worker Cloudflare. Host `linkbioworker-api.{env}.cfw.actuar.cloud`. */
  linkbio_service: string;
  /** Address suggestions — Cloudflare Worker proxying Google Places. Host `geoworker-api.{env}.cfw.actuar.cloud`. */
  geo_service: string;
  /**
   * Comunicados — Worker Cloudflare (`Actuar/announcementsworker-api`). Fila e
   * registro de visto do overlay, e o CRUD da tela de comunicados.
   *
   * Publicação ÚNICA em produção (ADR 0004 do Worker): os cinco ambientes do
   * front apontam para o mesmo host, como `suggestion_service`,
   * `indications_service` e `print_agent_service`. Quando houver um par de
   * homologação, é aqui que se separa — o front nunca monta esta URL à mão.
   */
  announcements_service: string;
  /**
   * Agenda de instalação do novo ACESSO (EdgeDesk / AccessDesk) — Worker
   * Cloudflare `Actuar/schedulertoletusapi-worker`. Convite, grade de horários,
   * agendamento da unidade e o painel do suporte (`/admin/*`).
   *
   * Publicação ÚNICA: os cinco ambientes apontam para o mesmo host, como no
   * sistema antigo. O nome do Worker é a URL — renomear quebra os cinco.
   */
  access_scheduling_service: string;
  /**
   * Agenda de instalação do novo agente de IMPRESSÃO (print-agent) — Worker
   * Cloudflare `Actuar/schedulerprintagentapi-worker`. Mesmo contrato da agenda
   * do acesso, sem o campo `produto`.
   */
  print_scheduling_service: string;
  /**
   * `Toletus.AccessEdgeProvisioning` — pareamento do AccessDesk. Host próprio
   * (`accessedgeprovisioning-api`), separado do `access_edge_manager_service`.
   */
  access_edge_provisioning_service: string;
  /**
   * `Actuar.ChannelGateway` — conexões de canal (WhatsApp Cloud API) e catálogo de
   * templates. Superfície REST: CRUD do pack, `probe`, `announce`, `submit`,
   * `import` e `onboard` (Embedded Signup).
   */
  channel_gateway_service: string;
  /** Controller OData do ChannelGateway (`/OData/{Set}` — leitura de coleção). */
  odata_channel_gateway_service: string;
  /**
   * `Actuar.SupportService` — atendimento (conversas, desks, agentes).
   *
   * O ChannelGateway usa só a listagem de workspaces, para o cadastro de conexão
   * não precisar de GUID digitado.
   */
  support_service: string;
  /**
   * `Actuar.AtoService` — o ato: resumos de conversa do atendimento pela Claude API (item 25 do
   * ato · Workspace). Chamado com o token do agente; lê a conversa no SupportService com ele.
   */
  ato_service: string;
};

export type ExternalUrlsType = {
  pague_la: string;
  /**
   * Host of the new Pague.lá app (`Actuar/paguela-app-v2`), where a card is typed and saved:
   * `{pague_la_checkout}/checkout/add`. Not `pague_la`, which is the legacy app and has no
   * such page.
   *
   * It has to be the build that talks to the Pague.lá API this environment charges through
   * (`apiUrl.paguela`): a card saved through another API does not exist for the subscription
   * it is put on.
   *
   * The page answers only to the origins it lists: today the production shells, and the
   * `rc` shells of Actuar, Ediz and Tickou. From anywhere else "Adicionar cartão" ends on
   * that page's refusal — on the operator's form, after the subscription has been saved.
   * That is `development`, whose API no build of that app talks to; `stage`, whose listing
   * is an open decision there (Actuar/paguela-app-v2#34); and the `rc` shells FácilFit and
   * Ediz have of their own, whose hosts nobody has named.
   */
  pague_la_checkout: string;
  sign_view: string;
  /**
   * Domínio público das páginas do hotsite — o subdomínio do inquilino é
   * prefixado (`{sub}.{hotsite_domain}/{path}`).
   *
   * Mora aqui, e não numa condição sobre `environment`, porque tem de acompanhar
   * a API de onde o subdomínio é lido: `local` e `rc` consomem o hotsite de
   * produção, e a condição antiga mandava os dois para homologação — endereço
   * que existe, resolve e não tem a página.
   */
  hotsite_domain: string;
  /**
   * Actuar short domain of `Actuar/linkshortener-api` — the `LinkShortenerService`
   * default, paired with `sourceApi: 'actuar'`. The host both issues the code and
   * prefixes the short link, so the two always travel together: a mixed pair hands
   * out a dead link.
   *
   * Same host in all five environments — only production has custom short domains
   * (declared in the shortener's helm values), like `weather_service` and
   * `suggestion_service`. When staging gets one, the place to split it is here.
   */
  link_shortener_actuar: string;
  /**
   * Short domain of the Ediz products — serves both the app links and the Live
   * Pickup panel links, which are told apart by `sourceApi`, not by the host.
   */
  link_shortener_ediz: string;
  /**
   * Origin of the public self-registration app (`apps/registration-app`), where
   * a gym's address is `{registration_app}/{slug}`.
   *
   * It lives here because the panel has to print the link it hands out, and that
   * app is deployed on its own Cloudflare Pages project, not under the panel's
   * host. Today these are the default `*.pages.dev` hosts of the two Pages
   * projects; a custom domain replaces the value here and nowhere else.
   */
  registration_app: string;
};

export const ProductionEnv: ApiUrlsType = API_PRODUCTION_ACTUAR;
export const DevelopmentEnv: ApiUrlsType = API_DEVELOPMENT_ACTUAR;
export const ReleaseEnv: ApiUrlsType = API_RELEASE_ACTUAR;
export const LocalEnv: ApiUrlsType = API_LOCAL_ACTUAR;
/**
 * TEMPORÁRIO — stage a consumir as APIs de PRODUÇÃO.
 *
 * `API_STAGE_ACTUAR` continua intacto em `./env/environment.stage.ts`; só o
 * alias deixou de o usar. Reverter é devolver esta linha a `API_STAGE_ACTUAR`
 * e repor o import.
 *
 * Enquanto isto estiver na main, tudo o que for feito num build de stage —
 * plano criado, cobrança gerada, mensagem disparada — escreve na base de
 * produção. Só `apiUrl` mudou: `StageAppUrl` e `StageExternalUrl` continuam
 * em stage, portanto o front testado continua a ser o de stage.
 */
export const StageEnv: ApiUrlsType = API_PRODUCTION_ACTUAR;
export const LocalAppUrl: APP_URLType = APP_URL_LOCAL;
export const ProductionAppUrl: APP_URLType = APP_URL_PRODUCTION;
export const DevelopmentAppUrl: APP_URLType = APP_URL_DEVELOPMENT;
export const StageAppUrl: APP_URLType = APP_URL_STAGE;
export const ReleaseAppUrl: APP_URLType = APP_URL_RELEASE;
export const LocalExternalUrl = EXTERNAL_URL_LOCAL;
export const ProductionExternalUrl = EXTERNAL_URL_PRODUCTION;
export const DevelopmentExternalUrl = EXTERNAL_URL_DEVELOPMENT;
export const StageExternalUrl = EXTERNAL_URL_STAGE;
export const ReleaseExternalUrl = EXTERNAL_URL_RELEASE;

export const APP_CONFIG = new InjectionToken<AppConfig>('APP_CONFIG');

export const EXTERNAL_APIS = { ipApi: 'https://ipapi.co/json' };

export const GOOGLE_MAPS_CONFIG = {
  apiKey: 'AIzaSyCjETHiVAXDHYEb7gXbS9O7dTgMnyndZEQ',
  libraries: ['places'],
};

/**
 * Meta Embedded Signup — a única parte que é escolha do front.
 *
 * `appId`, `configId` e as versões do fluxo saíram daqui: vêm de
 * `GET /ChannelConnection/onboarding-config`, porque andam em par com o app
 * provedor e ele muda sem o front saber (aconteceu em 04/08, da Actuar para a
 * Toletus). Fixados no bundle, a troca virava deploy de front — e falhava em
 * silêncio, com o popup abrindo contra o app velho.
 *
 * Nada aqui é segredo. O `code` que o popup devolve só vale acompanhado do app
 * secret, e essa troca é servidor-a-servidor dentro do ChannelGateway — o front
 * nunca vê, digita ou transporta access token.
 */
export const META_EMBEDDED_SIGNUP_CONFIG = {
  /** Versão do SDK JS servida por connect.facebook.net. */
  sdkVersion: 'v23.0',
};

export const AppConfigProvider = (config: AppConfig): ValueProvider => ({
  provide: APP_CONFIG,
  useValue: {
    ...config,
    enabledPlatformFeatures: config.enabledPlatformFeatures ?? [],
  },
});

export const adminTenants = [
  {
    account: 'd2467ad2-8f94-48e8-a938-8355af6b55da',
    name: 'K11',
    blipPassword: '12345',
  },
  {
    account: 'eb7689de-62b1-4e1b-98aa-86ee648bfe91',
    name: 'Actuar',
  },
  {
    account: '77a6bd71-6836-49e7-9f1b-90403fbca069',
    name: 'Treino',
  },
  {
    account: 'f8f293ac-1498-438a-9e60-11d1e8a36c0d',
    name: 'Afig',
  },
  {
    account: '9546e583-6fb6-46bc-aa5f-e49f202d9397',
    name: 'Catracaparaacademia',
  },
  {
    account: '44cc6127-5197-401d-a68d-3bc9a2f4fe3e',
    name: 'Paguela',
  },
  {
    account: '09d6f13e-80c9-4090-b493-6ad3a0ffab1d',
    name: 'ActPaguela',
  },
  {
    account: '95a38624-54bd-4042-8b4e-bfcc3096fb08',
    name: 'ActuarPaguela',
  },
  {
    account: '9c1c84e1-b076-4721-bf60-815d5a208e12',
    name: 'Facilfit',
  },
  {
    account: '8aa780a2-e516-42c4-a928-3b1802fbffdc',
    name: 'Toletus',
  },
  {
    account: '0b6ab3f7-319d-46e3-bc5c-3f32e056fef6',
    name: 'ACT',
  },
  {
    account: '019d0295-80cc-7990-97cb-0186ec828860',
    name: 'Ediz',
  },
  {
    account: '019d7338-92a0-7380-99f0-1db0c9714275',
    name: 'Tickou',
  },
];

// Allowlist that gates the "full" treasury experience (Caixas, plano de contas
// tree/DRE, reconciliation providers and connections).
export const treasuryFullEnabledTenants = [
  { account: '019e0279-b275-7453-a55e-a1b45433b694', name: 'academiaTeste' },
  { account: '512944e0-13b3-4d35-ac0b-c3bee678548d', name: 'Oficial 1' },
  { account: 'bbda8308-2042-4e5b-be28-a8b2c4819e1c', name: 'Oficial 2' },
  { account: 'd2467ad2-8f94-48e8-a938-8355af6b55da', name: 'K11' },
];

export function isTreasuryFullEnabledTenant(
  tenantId: string | undefined | null
): boolean {
  return treasuryFullEnabledTenants.some((t) => t.account === tenantId);
}

/**
 * Gates the Academy as AUDIENCE. Every client tenant of every brand (Actuar,
 * FacilFit, Ediz, Tickou) watches the Academy, so the only thing left to check
 * is that there is a tenant at all — an anonymous session has no audience.
 *
 * It stays a function on purpose: this is the single seam to restrict the
 * Academy again (an allowlist, a platform feature, a system module) without
 * touching the route guard, the navbar, the live dock or the decks store, which
 * all call it. Producer screens are a different question — `GET /Capabilities/me`
 * (`AcademyCapabilitiesStore` in `@domains/academy/data`).
 */
export function isAcademyEnabledTenant(
  tenantId: string | undefined | null
): boolean {
  return !!tenantId;
}

/**
 * Gates the Link na Bio (Marketing). Released to every client tenant of every
 * brand, so the only thing left to check is that there is a tenant at all — an
 * anonymous session has no builder to open.
 *
 * It stays a function on purpose: this is the single seam to restrict the Link
 * na Bio again (an allowlist, a platform feature) without touching the route
 * guard or the navbar, which both call it. Whether the tenant bought the module
 * is a different question and stays where it was: `SystemModules.CRM` on the
 * route and on the sidebar item. Restricting it again also means putting
 * `link-bio` back in the footer catalog's `EXCLUDED_IDS`, which reads the
 * sidebar and cannot see a tenant rule.
 */
export function isLinkBioEnabledTenant(
  tenantId: string | undefined | null
): boolean {
  return !!tenantId;
}

// Allowlist that gates the internal side of the public self-registration: the
// registration links, the registrations they received and the counter recovery
// on the customers list.
export const publicRegistrationEnabledTenants = [
  { account: 'd2467ad2-8f94-48e8-a938-8355af6b55da', name: 'K11' },
];

/**
 * Gates the public self-registration inside the platform. Matched on the
 * tenant id, never on an `adminTenants` index, and with no admin escape: the
 * Actuar's own accounts do not see it either.
 *
 * The single seam the route guard, the sidebar, the footer catalog and the
 * customers list all call. Whether the brand ships the feature is a different
 * question and stays where it was: `PLATFORM_FEATURES.publicRegistration`.
 * Releasing it to every tenant also means taking `registration-link` and
 * `link-registrations` out of the footer catalog's `EXCLUDED_IDS`, which reads
 * the sidebar and cannot see a tenant rule.
 */
export function isPublicRegistrationEnabledTenant(
  tenantId: string | undefined | null
): boolean {
  return publicRegistrationEnabledTenants.some((t) => t.account === tenantId);
}

/** True when the tenant is on the `adminTenants` allowlist (internal/admin accounts). */
export function isAdminTenant(tenantId: string | undefined | null): boolean {
  if (!tenantId) {
    return false;
  }
  return adminTenants.some((t) => t.account === tenantId);
}

export const devExtremeKey =
  'ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogIjAwNDVjNWIzLWM3NGQtNDFhNi05MzE5LTRjY2M0YWVkNzU3YyIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjQyCn0=.ZxM4fzXdIr+ZYNdrF+I9hvM3grAjT3RPckE0ffxHO8BHEUdDB+v+ABNP+/U+0Z7jd3jwgOmth55B58xy+Zz3R9U7ntDpbw1xxM/gA/VxsUNO3R+GvGFDfjtn56E3QcZTvGzaKQ==';

export const sentryDsn =
  'https://13d7f73cb5b4784978550a46eb7c2d64@o4511513730809856.ingest.us.sentry.io/4511531573444608';
